import { useEffect, useMemo, useState } from 'react'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../utils/constants'
import {
  alertHeadline,
  formatImpactAreas,
  toggleImpactArea,
  type AlertPayload,
  type ImpactFeedback,
} from './supplierRiskAlertShared'

const field =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/20'

type Options = {
  impactAreas: string[]
  supplyStatuses: string[]
  durations: string[]
}

type SupplierLine = {
  id: string
  supplier_name: string
  unique_id?: string | null
  line_status: string
}

type LineAnswers = {
  impactAreas: string[]
  supplyStatus: string
  impactDuration: string
  impactDetails: string
}

function emptyAnswers(options: Options): LineAnswers {
  return {
    impactAreas: [],
    supplyStatus: options.supplyStatuses[0],
    impactDuration: options.durations[0],
    impactDetails: '',
  }
}

function QuestionBlock({
  options,
  answers,
  onChange,
  namePrefix,
}: {
  options: Options
  answers: LineAnswers
  onChange: (patch: Partial<LineAnswers>) => void
  namePrefix: string
}) {
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="text-sm font-medium">Area(s) of potential impact</legend>
        <p className="mt-1 text-xs text-slate-500">Select all that apply</p>
        <div className="mt-2 space-y-2">
          {options.impactAreas.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                name={`${namePrefix}-impactAreas`}
                checked={answers.impactAreas.includes(o)}
                onChange={() => onChange({ impactAreas: toggleImpactArea(answers.impactAreas, o) })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-sm font-medium">Supply / service status</legend>
        <div className="mt-2 space-y-2">
          {options.supplyStatuses.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${namePrefix}-supplyStatus`}
                checked={answers.supplyStatus === o}
                onChange={() => onChange({ supplyStatus: o })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-sm font-medium">Expected impact duration</legend>
        <div className="mt-2 space-y-2">
          {options.durations.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${namePrefix}-impactDuration`}
                checked={answers.impactDuration === o}
                onChange={() => onChange({ impactDuration: o })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="block text-sm">
        <span className="font-medium">Impact details</span>
        <textarea
          className={`${field} mt-1`}
          required
          maxLength={250}
          rows={4}
          value={answers.impactDetails}
          onChange={(e) => onChange({ impactDetails: e.target.value })}
          placeholder="Short summary of expected impact…"
        />
        <span className="text-xs text-slate-500">{answers.impactDetails.length}/250</span>
      </label>
    </div>
  )
}

function ReadOnlyAnswers({ feedback }: { feedback: ImpactFeedback }) {
  return (
    <dl className="space-y-2 text-sm text-slate-700">
      <div>
        <dt className="text-slate-500">Area(s) of potential impact</dt>
        <dd className="font-medium">{formatImpactAreas(feedback)}</dd>
      </div>
      <div>
        <dt className="text-slate-500">Supply / service status</dt>
        <dd className="font-medium">{feedback.supply_status}</dd>
      </div>
      <div>
        <dt className="text-slate-500">Expected impact duration</dt>
        <dd className="font-medium">{feedback.impact_duration}</dd>
      </div>
      <div>
        <dt className="text-slate-500">Impact details</dt>
        <dd className="font-medium whitespace-pre-wrap">{feedback.impact_details}</dd>
      </div>
    </dl>
  )
}

export function RiskAlertFeedbackPage() {
  const token = new URLSearchParams(window.location.search).get('token') || ''
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [options, setOptions] = useState<Options | null>(null)
  const [payload, setPayload] = useState<AlertPayload | null>(null)
  const [feedbackMode, setFeedbackMode] = useState<'event' | 'per_supplier'>('event')
  const [suppliers, setSuppliers] = useState<SupplierLine[]>([])
  const [existingFeedback, setExistingFeedback] = useState<ImpactFeedback[]>([])
  const [already, setAlready] = useState(false)
  const [done, setDone] = useState(false)
  const [progressMsg, setProgressMsg] = useState('')
  const [eventAnswers, setEventAnswers] = useState<LineAnswers | null>(null)
  const [lineAnswers, setLineAnswers] = useState<Record<string, LineAnswers>>({})
  const [selectedLineIds, setSelectedLineIds] = useState<Record<string, boolean>>({})
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const title = useMemo(() => (payload ? alertHeadline(payload) : 'Supplier alert'), [payload])

  const feedbackByLine = useMemo(() => {
    const map = new Map<string, ImpactFeedback>()
    for (const f of existingFeedback) {
      if (f.supplier_line_id) map.set(f.supplier_line_id, f)
    }
    return map
  }, [existingFeedback])

  const eventFeedback = useMemo(
    () => existingFeedback.find((f) => !f.supplier_line_id) || null,
    [existingFeedback],
  )

  const receivedCount = suppliers.filter((s) => s.line_status === 'received').length
  const pendingSuppliers = suppliers.filter((s) => s.line_status !== 'received')

  async function loadForm() {
    const res = await fetch(
      `${SUPABASE_URL}/functions/v1/submit-impact-feedback?token=${encodeURIComponent(token)}`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } },
    )
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load form')
    if (data.alreadySubmitted) {
      setAlready(true)
      setPayload(data.alert?.payload || null)
      setSuppliers(data.suppliers || [])
      setExistingFeedback(data.feedback || [])
      return
    }
    setOptions(data.options)
    setPayload(data.alert?.payload || null)
    const mode = data.alert?.feedbackMode === 'per_supplier' ? 'per_supplier' : 'event'
    setFeedbackMode(mode)
    setSuppliers(data.suppliers || [])
    setExistingFeedback(data.feedback || [])
    const opts = data.options as Options
    setEventAnswers(emptyAnswers(opts))
    const nextAnswers: Record<string, LineAnswers> = {}
    const nextSelected: Record<string, boolean> = {}
    for (const s of (data.suppliers || []) as SupplierLine[]) {
      if (s.line_status !== 'received') {
        nextAnswers[s.id] = emptyAnswers(opts)
        nextSelected[s.id] = true
      }
    }
    setLineAnswers(nextAnswers)
    setSelectedLineIds(nextSelected)
  }

  useEffect(() => {
    if (!token) {
      setError('Missing feedback token.')
      setLoading(false)
      return
    }
    ;(async () => {
      try {
        await loadForm()
      } catch (e: any) {
        setError(e.message || 'Failed to load')
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function onSubmitEvent(e: React.FormEvent) {
    e.preventDefault()
    if (!eventAnswers) return
    if (!eventAnswers.impactAreas.length) {
      setError('Select at least one Area of potential impact.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-impact-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          token,
          impactAreas: eventAnswers.impactAreas,
          supplyStatus: eventAnswers.supplyStatus,
          impactDuration: eventAnswers.impactDuration,
          impactDetails: eventAnswers.impactDetails,
          submittedByEmail: email,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Submit failed')
      setDone(true)
      setProgressMsg('Thank you. Your feedback was sent to TPRM governance and Early Signals.')
    } catch (err: any) {
      setError(err.message || 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitPerSupplier(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const responses = pendingSuppliers
        .filter((s) => selectedLineIds[s.id])
        .map((s) => {
          const a = lineAnswers[s.id]
          return {
            supplierLineId: s.id,
            impactAreas: a?.impactAreas || [],
            supplyStatus: a?.supplyStatus,
            impactDuration: a?.impactDuration,
            impactDetails: a?.impactDetails,
          }
        })
        .filter((r) => String(r.impactDetails || '').trim() && (r.impactAreas?.length || 0) > 0)

      if (!responses.length) {
        throw new Error(
          'Fill in at least one pending supplier (including Area(s) of potential impact) and keep it selected.',
        )
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-impact-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          token,
          responses,
          submittedByEmail: email,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Submit failed')

      if (data.complete) {
        setDone(true)
        setProgressMsg('Thank you. All supplier assessments are complete.')
      } else {
        setProgressMsg(
          `Saved. ${data.received} of ${data.total} suppliers complete. You can return with this link for the rest.`,
        )
        setLoading(true)
        await loadForm()
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message || 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  const showForm = !loading && !error && !already && !done && options

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-lg mx-auto px-4 py-10">
        <h1 className="text-2xl font-semibold">Supplier risk impact assessment</h1>
        <p className="text-sm text-slate-600 mt-1">{title}</p>
        {feedbackMode === 'per_supplier' && suppliers.length > 0 && !already && !done && (
          <p className="text-sm text-slate-600 mt-1">
            {receivedCount} of {suppliers.length} suppliers complete. Provide impact for each supplier
            below — you can submit one or more at a time.
          </p>
        )}
        {feedbackMode === 'event' && suppliers.length > 1 && showForm && (
          <p className="text-sm text-slate-600 mt-1">
            Suppliers in this event: {suppliers.map((s) => s.supplier_name).join(', ')}. Answer once
            for the whole event.
          </p>
        )}

        {loading && <p className="mt-6 text-sm">Loading…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
        )}
        {progressMsg && !error && (
          <p className="mt-6 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            {progressMsg}
          </p>
        )}
        {(already || done) && !error && !progressMsg && (
          <p className="mt-6 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            {already
              ? 'Feedback was already submitted for this alert. Thank you.'
              : 'Thank you. Your feedback was sent to TPRM governance and Early Signals.'}
          </p>
        )}

        {showForm && feedbackMode === 'event' && eventAnswers && (
          <form onSubmit={onSubmitEvent} className="mt-6 space-y-5 bg-white border border-slate-200 rounded-xl p-5">
            <QuestionBlock
              options={options}
              answers={eventAnswers}
              onChange={(patch) => setEventAnswers((prev) => ({ ...(prev as LineAnswers), ...patch }))}
              namePrefix="event"
            />
            <label className="block text-sm">
              <span className="font-medium">Your email (optional)</span>
              <input className={`${field} mt-1`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-black text-white py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {busy ? 'Submitting…' : 'Submit feedback'}
            </button>
          </form>
        )}

        {showForm && feedbackMode === 'per_supplier' && (
          <form onSubmit={onSubmitPerSupplier} className="mt-6 space-y-4">
            {suppliers.map((s) => {
              const received = s.line_status === 'received'
              const fb = feedbackByLine.get(s.id) || (received ? eventFeedback : null)
              return (
                <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h2 className="text-base font-semibold">{s.supplier_name}</h2>
                      {s.unique_id && <p className="text-xs text-slate-500">{s.unique_id}</p>}
                    </div>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                        received ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
                      }`}
                    >
                      {received ? 'Received' : 'Pending'}
                    </span>
                  </div>
                  {received && fb ? (
                    <ReadOnlyAnswers feedback={fb} />
                  ) : (
                    <>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedLineIds[s.id])}
                          onChange={(e) =>
                            setSelectedLineIds((prev) => ({ ...prev, [s.id]: e.target.checked }))
                          }
                        />
                        <span>Include in this submit</span>
                      </label>
                      {selectedLineIds[s.id] && lineAnswers[s.id] && (
                        <QuestionBlock
                          options={options}
                          answers={lineAnswers[s.id]}
                          onChange={(patch) =>
                            setLineAnswers((prev) => ({
                              ...prev,
                              [s.id]: { ...prev[s.id], ...patch },
                            }))
                          }
                          namePrefix={s.id}
                        />
                      )}
                    </>
                  )}
                </div>
              )
            })}
            {pendingSuppliers.length > 0 && (
              <>
                <label className="block text-sm">
                  <span className="font-medium">Your email (optional)</span>
                  <input
                    className={`${field} mt-1`}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-lg bg-black text-white py-2.5 text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? 'Submitting…' : 'Submit selected suppliers'}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
